import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Assignment,
  Chat,
  CheckCircle,
  Schedule,
  Logout,
} from '@mui/icons-material';
import { 
  FiMessageSquare, 
  FiHeadphones, 
  FiUsers, 
  FiFileText, 
  FiMessageCircle, 
  FiBook, 
  FiEdit, 
  FiStar, 
  FiCheckCircle, 
  FiClock, 
  FiHelpCircle, 
  FiClipboard, 
  FiMessageSquare as FiCannedResponse,
  FiLayout,
  FiGitBranch,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats } from '../services/api';
import { mqttSupportService } from '../services/mqttSupportService';
import SupportTickets from './SupportTickets';
import SupportChatManagement from './SupportChatManagement';
import OnlineUsers from './OnlineUsers';
import AuditLogs from './AuditLogs';
import WhatsAppManagement from './WhatsAppManagement';
import HelpCenter from './HelpCenter';
import BlogManagement from './BlogManagement';
import UnifiedSurveyManagement from './UnifiedSurveyManagement';
import SupportActionRequestManagement from './SupportActionRequestManagement';
import SLAManagement from './SLAManagement';
import FAQManagement from './FAQManagement';
import ChatbotConversations from './ChatbotConversations';
import TermsAndConditions from './TermsAndConditions';
import CannedResponseManagement from './CannedResponseManagement';
import UserJourneyManagement from './UserJourneyManagement';
import ThemeToggle from './ThemeToggle';
import QuickActionsToolbar from './QuickActionsToolbar';
import ShortcutsHelp from './ShortcutsHelp';
import { useShortcut } from '../contexts/KeyboardShortcutsContext';
import './Dashboard.css';

interface DashboardStats {
  success?: boolean;
  totalTickets: number;
  resolvedTickets: number;
  activeChatRequests: number;
  pendingTickets?: number;
  timestamp?: string;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [statistics, setStatistics] = useState<DashboardStats>({
    totalTickets: 0,
    resolvedTickets: 0,
    activeChatRequests: 0,
    timestamp: '',
  });
  const [activeTab, setActiveTab] = useState<string>('tickets');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mqttConnected, setMqttConnected] = useState(false);

  // Register keyboard shortcuts for navigation
  useShortcut({
    key: 'g',
    shift: true,
    description: 'Go to Tickets',
    action: () => setActiveTab('tickets'),
  }, []);

  useShortcut({
    key: 'c',
    shift: true,
    description: 'Go to Chat',
    action: () => setActiveTab('chat'),
  }, []);

  useShortcut({
    key: 'd',
    shift: true,
    description: 'Go to Dashboard',
    action: () => setActiveTab('tickets'),
  }, []);

  const loadData = useCallback(async () => {
    try {
      setRefreshing(true);
      const statsData = await getDashboardStats();

      if (statsData && statsData.success !== false) {
        setStatistics({
          success: statsData.success,
          totalTickets: statsData.totalTickets || 0,
          resolvedTickets: statsData.resolvedTickets || 0,
          activeChatRequests: statsData.activeChatRequests || 0,
          pendingTickets: statsData.pendingTickets || 0,
          timestamp: new Date().toISOString(),
        });
      } else {
        console.error('Failed to fetch dashboard stats:', statsData?.error || 'Unknown error');
        setStatistics({
          totalTickets: 0,
          resolvedTickets: 0,
          activeChatRequests: 0,
          pendingTickets: 0,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      setStatistics({
        totalTickets: 0,
        resolvedTickets: 0,
        activeChatRequests: 0,
        pendingTickets: 0,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const handleQuickNewTicket = useCallback(() => {
    window.dispatchEvent(new CustomEvent('triggerNewTicket'));
  }, []);

  useEffect(() => {
    loadData();

    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const connCallbackRef = React.useRef<((connected: boolean) => void) | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      console.log('🔌 [Dashboard] Connecting to MQTT...');

      connCallbackRef.current = (connected) => {
        console.log('📡 [Dashboard] MQTT connection status changed:', connected ? 'Connected ✅' : 'Disconnected ❌');
        setMqttConnected(connected);
      };

      mqttSupportService.onConnectionStatusChange(connCallbackRef.current);
      mqttSupportService.connect(token);

      const initialCheck = setTimeout(() => {
        const connected = mqttSupportService.isConnectedToBroker();
        console.log('📡 [Dashboard] Initial MQTT status:', connected ? 'Connected ✅' : 'Disconnected ❌');
        setMqttConnected(connected);
      }, 2000);

      const checkConnection = setInterval(() => {
        const connected = mqttSupportService.isConnectedToBroker();
        setMqttConnected(connected);
      }, 5000);

      return () => {
        clearTimeout(initialCheck);
        clearInterval(checkConnection);
        if (connCallbackRef.current) {
          mqttSupportService.offConnectionStatusChange(connCallbackRef.current);
        }
      };
    } else {
      console.warn('⚠️ [Dashboard] No admin token found, cannot connect to MQTT');
      setMqttConnected(false);
    }
  }, []);

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
  }> = ({ title, value, icon }) => {
    const displayValue = (value ?? 0).toLocaleString();
    return (
      <Card className="stat-card">
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1}>
              <Typography 
                variant="h3" 
                component="div" 
                fontWeight="700" 
                color="var(--text-primary)" 
                sx={{ mb: 0.5, fontSize: '2rem' }}
              >
                {displayValue}
              </Typography>
              <Typography 
                variant="body2" 
                color="var(--text-secondary)" 
                fontWeight="500" 
                sx={{ fontSize: '13px' }}
              >
                {title}
              </Typography>
            </Box>
            <Box sx={{ color: 'var(--primary-500)', opacity: 0.15 }}>{icon}</Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="customer-support-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <Box className="header-left">
          <Typography variant="h5" component="h1" fontWeight="bold">
            Customer Support Platform
          </Typography>
        </Box>
        <Box className="header-right">
          {/* Quick Actions Toolbar */}
          <QuickActionsToolbar
            onNewTicket={handleQuickNewTicket}
            onRefresh={loadData}
            loading={refreshing}
          />

          <Box
            className={`mqtt-status-indicator ${mqttConnected ? 'online' : 'offline'}`}
          >
            <span className="status-dot"></span>
            {mqttConnected ? 'Online' : 'Offline'}
          </Box>

          {/* Theme Toggle */}
          <ThemeToggle />

          {user && (
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mr: 1 }}>
              {user.name}
            </Typography>
          )}
          <IconButton
            onClick={logout}
            title="Logout"
            sx={{ color: 'var(--text-secondary)' }}
          >
            <Logout />
          </IconButton>
        </Box>
      </header>

      {/* Statistics Cards */}
      <Box className="stats-section">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          <StatCard
            title="Total Tickets"
            value={statistics.totalTickets}
            icon={<Assignment sx={{ fontSize: 40 }} />}
          />
          <StatCard
            title="Resolved"
            value={statistics.resolvedTickets}
            icon={<CheckCircle sx={{ fontSize: 40 }} />}
          />
          <StatCard
            title="Active Chats"
            value={statistics.activeChatRequests}
            icon={<Chat sx={{ fontSize: 40 }} />}
          />
          <StatCard
            title="Pending"
            value={statistics.pendingTickets || 0}
            icon={<Schedule sx={{ fontSize: 40 }} />}
          />
        </Box>
      </Box>

      {/* Sidebar Navigation */}
      <Box className="dashboard-layout">
        <Box className="sidebar-navigation">
          <div className="sidebar-header">
            <h3>Navigation</h3>
          </div>
          <nav className="sidebar-menu">
            <button
              className={`sidebar-item ${activeTab === 'tickets' ? 'active' : ''}`}
              onClick={() => setActiveTab('tickets')}
            >
              {React.createElement(FiMessageSquare as any, { className: "sidebar-icon" })}
              <span>Support Tickets</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              {React.createElement(FiHeadphones as any, { className: "sidebar-icon" })}
              <span>Chat Support</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              {React.createElement(FiUsers as any, { className: "sidebar-icon" })}
              <span>Online Users</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              {React.createElement(FiFileText as any, { className: "sidebar-icon" })}
              <span>Audit Logs</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'whatsapp' ? 'active' : ''}`}
              onClick={() => setActiveTab('whatsapp')}
            >
              {React.createElement(FiMessageCircle as any, { className: "sidebar-icon" })}
              <span>WhatsApp</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'help-center' ? 'active' : ''}`}
              onClick={() => setActiveTab('help-center')}
            >
              {React.createElement(FiBook as any, { className: "sidebar-icon" })}
              <span>Help Center</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'blogs' ? 'active' : ''}`}
              onClick={() => setActiveTab('blogs')}
            >
              {React.createElement(FiEdit as any, { className: "sidebar-icon" })}
              <span>Blogs</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'surveys' ? 'active' : ''}`}
              onClick={() => setActiveTab('surveys')}
            >
              {React.createElement(FiStar as any, { className: "sidebar-icon" })}
              <span>Reviews & Surveys</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'support-actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('support-actions')}
            >
              {React.createElement(FiCheckCircle as any, { className: "sidebar-icon" })}
              <span>Action Requests</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'sla-management' ? 'active' : ''}`}
              onClick={() => setActiveTab('sla-management')}
            >
              {React.createElement(FiClock as any, { className: "sidebar-icon" })}
              <span>SLA Management</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'faq' ? 'active' : ''}`}
              onClick={() => setActiveTab('faq')}
            >
              {React.createElement(FiHelpCircle as any, { className: "sidebar-icon" })}
              <span>FAQ Management</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'chatbot-conversations' ? 'active' : ''}`}
              onClick={() => setActiveTab('chatbot-conversations')}
            >
              {React.createElement(FiLayout as any, { className: "sidebar-icon" })}
              <span>Bot Conversations</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'terms' ? 'active' : ''}`}
              onClick={() => setActiveTab('terms')}
            >
              {React.createElement(FiClipboard as any, { className: "sidebar-icon" })}
              <span>Terms & Conditions</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'canned-responses' ? 'active' : ''}`}
              onClick={() => setActiveTab('canned-responses')}
            >
              {React.createElement(FiCannedResponse as any, { className: "sidebar-icon" })}
              <span>Canned Responses</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'user-journey' ? 'active' : ''}`}
              onClick={() => setActiveTab('user-journey')}
            >
              {React.createElement(FiGitBranch as any, { className: "sidebar-icon" })}
              <span>User Journey</span>
            </button>
          </nav>
        </Box>

        {/* Main Content */}
        <Box className="dashboard-content-wrapper">
          <Box className="dashboard-content">
            {activeTab === 'tickets' && <SupportTickets />}
            {activeTab === 'chat' && <SupportChatManagement />}
            {activeTab === 'users' && <OnlineUsers />}
            {activeTab === 'audit' && <AuditLogs />}
            {activeTab === 'whatsapp' && <WhatsAppManagement />}
            {activeTab === 'help-center' && <HelpCenter />}
            {activeTab === 'blogs' && <BlogManagement />}
            {activeTab === 'surveys' && <UnifiedSurveyManagement />}
            {activeTab === 'support-actions' && <SupportActionRequestManagement />}
            {activeTab === 'sla-management' && <SLAManagement />}
            {activeTab === 'faq' && <FAQManagement />}
            {activeTab === 'chatbot-conversations' && <ChatbotConversations />}
            {activeTab === 'terms' && <TermsAndConditions initialView="terms" />}
            {activeTab === 'canned-responses' && <CannedResponseManagement />}
            {activeTab === 'user-journey' && <UserJourneyManagement />}
          </Box>
        </Box>
      </Box>

      {/* Shortcuts Help Modal */}
      <ShortcutsHelp />
    </Box>
  );
};

export default Dashboard;
